/**
 * ExtranetPagePage — conteúdo completo de uma página KB restrita.
 * RPC kb_extranet_get_page já verifica ACL e loga auditoria.
 */
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import DOMPurify from 'dompurify';
import { useExtranetPage } from '@/hooks/kb/useKbExtranet';
import { Loader2, ChevronLeft, Calendar } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { KbPageReader } from '@/components/kb/KbPageReader';
import { markdownToTiptap } from '@/lib/kb/markdown';

export default function ExtranetPagePage() {
  const { tenantSlug = '', spaceSlug = '', pageSlug = '' } = useParams<{
    tenantSlug: string;
    spaceSlug: string;
    pageSlug: string;
  }>();
  const { data: page, isLoading, error } = useExtranetPage(tenantSlug, spaceSlug, pageSlug);

  const sanitizedHtml = useMemo(() => {
    if (!page?.content_html) return '';
    return DOMPurify.sanitize(page.content_html, { USE_PROFILES: { html: true } });
  }, [page?.content_html]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="space-y-4">
        <Link
          to={`/extranet/${tenantSlug}/espacos/${spaceSlug}`}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          Voltar
        </Link>
        <Card className="p-10 text-center">
          <h1 className="text-xl font-semibold">Página indisponível</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Você não tem permissão para acessar esta página ou ela não existe.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Helmet>
        <title>{page.title}</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <Link
        to={`/extranet/${tenantSlug}/espacos/${spaceSlug}`}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" />
        {page.space_name}
      </Link>

      <article className="prose prose-neutral dark:prose-invert max-w-none">
        <header className="not-prose mb-6">
          <h1 className="text-3xl font-bold tracking-tight">{page.title}</h1>
          {page.summary && (
            <p className="mt-2 text-lg text-muted-foreground">{page.summary}</p>
          )}
          {page.updated_at && (
            <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              Atualizado em{' '}
              {new Date(page.updated_at).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })}
            </div>
          )}
        </header>

        {/*
         * Renderização unificada com o editor: quando a página tiver
         * content_json (Tiptap doc) usamos o KbPageReader (mesmas extensions
         * e classes .kb-prose). Fallback para content_html (legacy) ou
         * markdown puro.
         */}
        {(page as any).content_json ? (
          <KbPageReader contentJson={(page as any).content_json} />
        ) : sanitizedHtml ? (
          <div
            className="kb-prose max-w-none"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          />
        ) : page.content_markdown ? (
          <KbPageReader contentJson={markdownToTiptap(page.content_markdown)} />
        ) : (
          <p className="text-muted-foreground">Conteúdo indisponível.</p>
        )}
      </article>
    </div>
  );
}
